import { UploadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Space, Typography, Upload, notification } from 'antd';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API } from '../../../common/constants';
import { auth } from '../../../config/firebase';

type InitInfo = {
    sheets: string[];
    notes: string[];
};

type InitCycleCount = {
    cycle_id: string;
    customers: number;
    assets: number;
    balances: number;
    rates: number;
};

type InitResponse = {
    cycles: InitCycleCount[];
    warnings: string[];
};

const PaymentCycleTools = () => {
    const { id } = useParams();
    const [info, setInfo] = useState<InitInfo | null>(null);
    const [infoError, setInfoError] = useState('');
    const [podio, setPodio] = useState<File | null>(null);
    const [balances, setBalances] = useState<File | null>(null);
    const [rates, setRates] = useState<File | null>(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState<InitResponse | null>(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setInfo(null);
            setInfoError('');
            setResult(null);
            try {
                const token = await auth.currentUser?.getIdToken();
                const response = await axios.get<InitInfo>(`${API}/api/paymentCycles/initReferenceInfo/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!cancelled) {
                    setInfo(response.data);
                }
            } catch (error) {
                if (!cancelled) {
                    setInfoError(await errorMessage(error));
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [id]);

    const ready = Boolean(podio && balances && rates && info);

    const run = async (download: boolean) => {
        if (!podio || !balances || !rates) {
            return;
        }
        setBusy(true);
        setResult(null);
        try {
            const body = new FormData();
            body.append('podio', podio);
            body.append('balances', balances);
            body.append('rates', rates);
            const token = await auth.currentUser?.getIdToken();
            const response = await axios.post(
                `${API}/api/paymentCycles/initReference/${id}${download ? '?download=1' : ''}`,
                body,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: download ? 'blob' : 'json',
                }
            );
            if (download) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `${id}_init.zip`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                notification.success({ message: 'CSV export downloaded' });
                return;
            }
            setResult(response.data as InitResponse);
            notification.success({ message: 'Reference data imported' });
        } catch (error) {
            notification.error({ message: 'Init failed', description: await errorMessage(error) });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%', padding: 16 }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
                Init reference data
            </Typography.Title>
            {infoError ? <Alert type="error" message={infoError} /> : null}
            {info ? (
                <Alert
                    type="info"
                    message="Expected files"
                    description={
                        <div>
                            <div>Podio workbook</div>
                            <ul>
                                {info.sheets.map((sheet) => (
                                    <li key={sheet}>{sheet}</li>
                                ))}
                            </ul>
                            <ul>
                                {info.notes.map((note) => (
                                    <li key={note}>{note}</li>
                                ))}
                            </ul>
                        </div>
                    }
                />
            ) : null}
            <Card>
                <Space direction="vertical">
                    <FileField label="Podio workbook" file={podio} onFile={setPodio} />
                    <FileField label="Balances" file={balances} onFile={setBalances} />
                    <FileField label="Rates" file={rates} onFile={setRates} />
                    <Space>
                        <Button type="primary" disabled={!ready} loading={busy} onClick={() => run(false)}>
                            Import
                        </Button>
                        <Button disabled={!ready} loading={busy} onClick={() => run(true)}>
                            Download CSVs
                        </Button>
                    </Space>
                </Space>
            </Card>
            {result ? (
                <Alert
                    type={result.warnings?.length ? 'warning' : 'success'}
                    message="Import finished"
                    description={
                        <div>
                            <ul>
                                {result.cycles.map((cycle) => (
                                    <li key={cycle.cycle_id}>
                                        {cycle.cycle_id}: {cycle.customers} customers, {cycle.assets} assets,{' '}
                                        {cycle.balances} balances, {cycle.rates} rates
                                    </li>
                                ))}
                            </ul>
                            {result.warnings?.length ? (
                                <ul>
                                    {result.warnings.map((warning) => (
                                        <li key={warning}>{warning}</li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                    }
                />
            ) : null}
        </Space>
    );
};

const FileField = ({
    label,
    file,
    onFile,
}: {
    label: string;
    file: File | null;
    onFile: (file: File) => void;
}) => (
    <Space>
        <Upload
            accept=".xlsx,.xls"
            maxCount={1}
            beforeUpload={(next) => {
                onFile(next);
                return false;
            }}
            onRemove={() => false}
            fileList={
                file
                    ? [{ uid: file.name, name: file.name, status: 'done' }]
                    : []
            }
        >
            <Button icon={<UploadOutlined />}>{label}</Button>
        </Upload>
    </Space>
);

async function errorMessage(error: unknown) {
    const response = (error as { response?: { data?: unknown } })?.response?.data;
    if (response instanceof Blob) {
        const text = await response.text();
        try {
            return JSON.parse(text).message || text;
        } catch {
            return text;
        }
    }
    if (response && typeof response === 'object' && 'message' in response) {
        return String((response as { message: unknown }).message);
    }
    if (error instanceof Error) {
        return error.message;
    }
    return 'Request failed';
}

export default PaymentCycleTools;
